import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { http, formatApiError, COMPANIES } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = (light) =>
  `mt-2 w-full bg-transparent border p-3 text-base normal-case tracking-normal outline-none transition-colors duration-200 ${
    light ? "border-gw-rule/40 focus:border-gw-rule text-gw-ink placeholder:text-gw-accent/40" : "border-white/60 focus:border-white text-white placeholder:text-white/40"
  }`;

export const EnquiryForm = ({ light = false, defaultCompany = COMPANIES[0], reference, testPrefix = "contact", onSent }) => {
  const empty = { name: "", email: "", phone: "", company: defaultCompany, message: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/enquiries", {
        ...form,
        ...(reference ? { ref_type: reference.type, ref_id: reference.id, ref_title: reference.title } : {}),
      });
      toast.success("Enquiry sent. We'll be in touch shortly.");
      setForm(empty);
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4" data-testid={`${testPrefix}-form`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="lab">Name
          <input required value={form.name} onChange={set("name")} className={field(light)} data-testid={`${testPrefix}-name-input`} />
        </label>
        <label className="lab">Email
          <input required type="email" value={form.email} onChange={set("email")} className={field(light)} data-testid={`${testPrefix}-email-input`} />
        </label>
      </div>
      <div className={`grid gap-4 ${reference ? "" : "sm:grid-cols-2"}`}>
        <label className="lab">Phone (optional)
          <input value={form.phone} onChange={set("phone")} className={field(light)} data-testid={`${testPrefix}-phone-input`} />
        </label>
        {!reference && (
          <div className="lab">Company
            <Select value={form.company} onValueChange={(v) => setForm((f) => ({ ...f, company: v }))}>
              <SelectTrigger data-testid={`${testPrefix}-company-select`}
                className={`mt-2 h-[50px] rounded-none normal-case tracking-normal text-base bg-transparent ${light ? "border-gw-rule/40 text-gw-ink" : "border-white/60 text-white"}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {COMPANIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`${testPrefix}-company-option-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <label className="lab">Message
        <textarea required rows={4} value={form.message} onChange={set("message")} className={field(light)}
          placeholder={reference ? `I'm interested in ${reference.title}...` : ""} data-testid={`${testPrefix}-message-input`} />
      </label>
      <button type="submit" disabled={busy} data-testid={`${testPrefix}-submit-button`}
        className={`group lab px-6 py-4 inline-flex justify-center items-center gap-3 transition-colors duration-200 disabled:opacity-60 ${
          light ? "bg-gw-btn text-gw-btnfg hover:bg-gw-btnhover" : "bg-white text-gw-onwhite hover:bg-gw-mist"}`}>
        {busy && <Loader2 size={14} className="animate-spin" />}
        Send enquiry <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
      </button>
    </form>
  );
};
